import React from 'react';

import classNames from 'classnames';
import { Wizard } from 'components/ClubComponents';
import Dropdown from 'components/cosmos/dropdown';
import { translate } from 'components/translate';
import PropTypes from 'prop-types';

import {
  BY_DATE_SELECTOR,
  SELECTOR,
  CUSTOM_DAYS_SELECTOR,
  BY_DAYS_SELECTOR,
  REMOVE_DRIPPING_SELECTOR,
  MASS_ACTION_SELECTOR,
  PREVIOUS,
  BEGIN
} from './constants';
import CustomDaysSelector from './CustomDaysSelector';
import DateSelector from './DateSelector';
import DaysSelector from './DaysSelector';
import MassSelector from './MassSelector';
import RemoveScheduleModal from './removeScheduleModal';
import Selector from './Selector';

import './MassSelector.scss';

const FLOW = {
  MASS_ACTION_SELECTOR: {
    on: {
      [SELECTOR]: SELECTOR
    }
  },
  [SELECTOR]: {
    on: {
      [PREVIOUS]: MASS_ACTION_SELECTOR,
      [BEGIN]: MASS_ACTION_SELECTOR,
      [BY_DAYS_SELECTOR]: BY_DAYS_SELECTOR,
      [BY_DATE_SELECTOR]: BY_DATE_SELECTOR,
      [REMOVE_DRIPPING_SELECTOR]: REMOVE_DRIPPING_SELECTOR
    }
  },
  [BY_DAYS_SELECTOR]: {
    on: {
      [PREVIOUS]: SELECTOR,
      [BEGIN]: MASS_ACTION_SELECTOR,
      [CUSTOM_DAYS_SELECTOR]: CUSTOM_DAYS_SELECTOR
    }
  },
  [BY_DATE_SELECTOR]: {
    on: {
      [PREVIOUS]: SELECTOR,
      [BEGIN]: MASS_ACTION_SELECTOR
    }
  },
  [CUSTOM_DAYS_SELECTOR]: {
    on: {
      [PREVIOUS]: BY_DAYS_SELECTOR,
      [BEGIN]: MASS_ACTION_SELECTOR
    }
  },
  [REMOVE_DRIPPING_SELECTOR]: {
    on: {
      [PREVIOUS]: SELECTOR
    }
  }
};

const MassActionWizard = ({
  disablePublish,
  disableUnpublish,
  t,
  checkedContent,
  publish,
  publishAndNotify,
  unpublish,
  exclude,
  button,
  onSuccess
}) => {
  const dropdownRef = React.useRef();
  const wizardRef = React.useRef();
  const [wizardState, setWizardState] = React.useState(MASS_ACTION_SELECTOR);
  const [openRemoveModal, setOpenRemoveModal] = React.useState(false);

  const closeMenu = () => {
    dropdownRef.current.closeMenu();
  };

  const updateWizardState = (state) => {
    if (state === BEGIN) {
      return setWizardState(FLOW[wizardState].on[BEGIN]);
    }

    if (state === PREVIOUS) {
      return setWizardState(FLOW[wizardState].on[PREVIOUS]);
    }

    setWizardState(state);
  };

  const getClassStep = (state) =>
    classNames({ '_d-block': wizardState === state });

  return (
    <>
      <Dropdown
        data-test="ca-scheduler"
        className="ca-scheduler"
        ref={dropdownRef}
      >
        {React.cloneElement(button, {
          slot: 'button'
        })}
        <Dropdown.MenuHeader className="_p-0">
          <Wizard
            id="MASS_ACTION_WIZARD"
            flow={FLOW}
            ref={wizardRef}
            active={MASS_ACTION_SELECTOR}
          >
            <Wizard.Step
              id={MASS_ACTION_SELECTOR}
              className={getClassStep(MASS_ACTION_SELECTOR)}
              headerless
            >
              {({ transition }) => (
                <MassSelector
                  transition={transition}
                  toogle={closeMenu}
                  publish={publish}
                  publishAndNotify={publishAndNotify}
                  unpublish={unpublish}
                  exclude={exclude}
                  disablePublish={disablePublish}
                  disableUnpublish={disableUnpublish}
                  updateWizardState={updateWizardState}
                />
              )}
            </Wizard.Step>

            <Wizard.Step
              id={SELECTOR}
              title={t('general.schedule')}
              className={getClassStep(SELECTOR)}
              headerless
            >
              {({ transition, options }) => (
                <Selector
                  transition={transition}
                  setOpenRemoveModal={setOpenRemoveModal}
                  toogle={closeMenu}
                  type={options.type}
                  hasPrevious
                  updateWizardState={updateWizardState}
                />
              )}
            </Wizard.Step>

            <Wizard.Step
              id={BY_DAYS_SELECTOR}
              className={getClassStep(BY_DAYS_SELECTOR)}
              headerless
            >
              {({ transition, options }) =>
                options.type ? (
                  <DaysSelector
                    type={options.type}
                    transition={transition}
                    checkedContent={checkedContent}
                    toogle={closeMenu}
                    updateWizardState={updateWizardState}
                  />
                ) : null
              }
            </Wizard.Step>

            <Wizard.Step
              id={BY_DATE_SELECTOR}
              className={getClassStep(BY_DATE_SELECTOR)}
              headerless
            >
              {({ transition, options }) => (
                <DateSelector
                  type={options.type}
                  checkedContent={checkedContent}
                  transition={transition}
                  toogle={closeMenu}
                  updateWizardState={updateWizardState}
                />
              )}
            </Wizard.Step>

            <Wizard.Step
              id={CUSTOM_DAYS_SELECTOR}
              className={getClassStep(CUSTOM_DAYS_SELECTOR)}
              headerless
            >
              {({ transition, options }) => (
                <CustomDaysSelector
                  type={options.type}
                  transition={transition}
                  checkedContent={checkedContent}
                  toogle={closeMenu}
                  updateWizardState={updateWizardState}
                />
              )}
            </Wizard.Step>
          </Wizard>
        </Dropdown.MenuHeader>
      </Dropdown>
      <RemoveScheduleModal
        open={openRemoveModal}
        onClose={() => setOpenRemoveModal(false)}
        checkedContent={checkedContent}
        onSuccess={onSuccess}
      />
    </>
  );
};

MassActionWizard.propTypes = {
  t: PropTypes.func,
  checkedContent: PropTypes.object,
  publish: PropTypes.func,
  publishAndNotify: PropTypes.func,
  publishClassName: PropTypes.string,
  unpublish: PropTypes.func,
  exclude: PropTypes.func,
  disableUnpublish: PropTypes.bool,
  disablePublish: PropTypes.bool,
  button: PropTypes.node,
  onSuccess: PropTypes.func
};

export default translate()(MassActionWizard);
